import { useState, useEffect, useCallback } from 'react'
import * as LucideIcons from 'lucide-react'
import { ETL_NODE_REGISTRY } from '../../data/etlNodeRegistry'
import { getDriver } from '../../data/etlDriverRegistry'
import {
  CONNECTION_SECTIONS,
} from '../../data/etlFileService'
import ConditionRowsEditor from './editors/ConditionRowsEditor'
import KeyPairsEditor from './editors/KeyPairsEditor'
import AggregationsEditor from './editors/AggregationsEditor'
import ColumnMappingEditor from './editors/ColumnMappingEditor'
import SortRowsEditor from './editors/SortRowsEditor'
import ExpressionListEditor from './editors/ExpressionListEditor'
import SchemaEditor from './editors/SchemaEditor'
import SqlScriptEditor from './editors/SqlScriptEditor'
import './EtlConfigPanel.css'

/**
 * Inline tags input component for array-of-string config fields.
 * Type a value and press Enter to add, Backspace on empty to remove last.
 */
function TagsInput({ value = [], onChange }) {
  const [inputValue, setInputValue] = useState('')

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault()
      onChange([...(value || []), inputValue.trim()])
      setInputValue('')
    }
    if (e.key === 'Backspace' && !inputValue && value?.length) {
      onChange(value.slice(0, -1))
    }
  }

  return (
    <div className="etl-tags-input">
      {(value || []).map((tag, i) => (
        <span key={i} className="etl-tag">
          {tag}
          <button
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          >
            <LucideIcons.X size={10} />
          </button>
        </span>
      ))}
      <input
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type and press Enter"
      />
    </div>
  )
}

function EtlConfigPanel({
  selectedNode,
  onConfigChange,
  onDeleteNode,
  executing,
  executionProgress,
  onOpenConnectionManager,
}) {
  const [expandedSections, setExpandedSections] = useState({})

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: prev[section] === false ? true : prev[section] === true ? false : false,
    }))
  }

  // Determine if this is a source or target node (connection manager applies)
  const nodeType = selectedNode?.data?.type || selectedNode?.data?.nodeType || selectedNode?.type
  const nodeDef = ETL_NODE_REGISTRY[nodeType] || {}
  const isSourceOrTarget = nodeDef.category === 'sources' || nodeDef.category === 'targets'

  // Connection info from snapshot
  const connSnapshot = selectedNode?.data?.connectionSnapshot || null
  const connName = connSnapshot?.name || null
  const connDriverId = connSnapshot?.driverId || null
  const connDriver = connDriverId ? getDriver(connDriverId) : null

  // No node selected - empty state
  if (!selectedNode) {
    return (
      <div className="etl-config-panel">
        <div className="etl-config-empty">
          <LucideIcons.MousePointerClick size={24} />
          <span>Select a node to configure</span>
        </div>
      </div>
    )
  }

  const config = selectedNode.data?.config || {}
  const progress = executionProgress?.[selectedNode.id]
  const result = progress?.result || selectedNode.data?.executionResult

  // Group config fields by section
  const configFields = nodeDef.configSchema || []
  const groupedFields = {}
  configFields.forEach((field) => {
    const section = field.section || 'Configuration'
    if (!groupedFields[section]) {
      groupedFields[section] = []
    }
    groupedFields[section].push(field)
  })

  // Columns available from upstream schema propagation
  const inputColumns = selectedNode.data?.inputSchema?.columns || []

  // Render a single config field based on its type
  const renderField = (field, value) => {
    const fieldValue = value !== undefined ? value : field.default

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={fieldValue || ''}
            onChange={(e) =>
              onConfigChange(selectedNode.id, field.key, e.target.value)
            }
            placeholder={field.placeholder || ''}
            disabled={executing}
          />
        )

      case 'number':
        return (
          <input
            type="number"
            value={fieldValue ?? ''}
            onChange={(e) =>
              onConfigChange(
                selectedNode.id,
                field.key,
                e.target.value === '' ? '' : Number(e.target.value)
              )
            }
            min={field.min}
            max={field.max}
            step={field.step}
            placeholder={field.placeholder || ''}
            disabled={executing}
          />
        )

      case 'password':
        return (
          <input
            type="password"
            value={fieldValue || ''}
            onChange={(e) =>
              onConfigChange(selectedNode.id, field.key, e.target.value)
            }
            placeholder={field.placeholder || ''}
            disabled={executing}
          />
        )

      case 'select':
        return (
          <select
            value={fieldValue || ''}
            onChange={(e) =>
              onConfigChange(selectedNode.id, field.key, e.target.value)
            }
            disabled={executing}
          >
            {(field.options || []).map((opt) => {
              const optValue =
                typeof opt === 'object' ? opt.value : opt
              const optLabel =
                typeof opt === 'object' ? opt.label : opt
              return (
                <option key={optValue} value={optValue}>
                  {optLabel}
                </option>
              )
            })}
          </select>
        )

      case 'checkbox':
        return (
          <label className="etl-checkbox">
            <input
              type="checkbox"
              checked={fieldValue || false}
              onChange={(e) =>
                onConfigChange(
                  selectedNode.id,
                  field.key,
                  e.target.checked
                )
              }
              disabled={executing}
            />
            <span>{field.label}</span>
          </label>
        )

      case 'textarea':
        return (
          <textarea
            value={fieldValue || ''}
            onChange={(e) =>
              onConfigChange(selectedNode.id, field.key, e.target.value)
            }
            placeholder={field.placeholder || ''}
            rows={field.rows || 4}
            disabled={executing}
          />
        )

      case 'tags':
        return (
          <TagsInput
            value={fieldValue || []}
            onChange={(val) =>
              onConfigChange(selectedNode.id, field.key, val)
            }
          />
        )

      case 'multiselect':
        return (
          <div className="etl-multiselect">
            {(field.options || []).map((opt) => {
              const optValue =
                typeof opt === 'object' ? opt.value : opt
              const optLabel =
                typeof opt === 'object' ? opt.label : opt
              const selected = (fieldValue || []).includes(optValue)
              return (
                <label key={optValue} className="etl-checkbox">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={(e) => {
                      const current = fieldValue || []
                      const next = e.target.checked
                        ? [...current, optValue]
                        : current.filter((v) => v !== optValue)
                      onConfigChange(selectedNode.id, field.key, next)
                    }}
                    disabled={executing}
                  />
                  <span>{optLabel}</span>
                </label>
              )
            })}
          </div>
        )

      case 'condition_rows':
        return (
          <ConditionRowsEditor
            value={fieldValue}
            onChange={(val) => onConfigChange(selectedNode.id, field.key, val)}
            disabled={executing}
            columns={inputColumns}
          />
        )

      case 'key_pairs':
        return (
          <KeyPairsEditor
            value={fieldValue}
            onChange={(val) => onConfigChange(selectedNode.id, field.key, val)}
            disabled={executing}
            leftLabel={field.leftLabel || 'Left Column'}
            rightLabel={field.rightLabel || 'Right Column'}
            columns={inputColumns}
          />
        )

      case 'aggregations':
        return (
          <AggregationsEditor
            value={fieldValue}
            onChange={(val) => onConfigChange(selectedNode.id, field.key, val)}
            disabled={executing}
            columns={inputColumns}
          />
        )

      case 'column_mapping':
        return (
          <ColumnMappingEditor
            value={fieldValue}
            onChange={(val) => onConfigChange(selectedNode.id, field.key, val)}
            disabled={executing}
            columns={inputColumns}
          />
        )

      case 'sort_rows':
        return (
          <SortRowsEditor
            value={fieldValue}
            onChange={(val) => onConfigChange(selectedNode.id, field.key, val)}
            disabled={executing}
            columns={inputColumns}
          />
        )

      case 'expression_list':
        return (
          <ExpressionListEditor
            value={fieldValue}
            onChange={(val) => onConfigChange(selectedNode.id, field.key, val)}
            disabled={executing}
          />
        )

      case 'sql_script':
        return (
          <SqlScriptEditor
            value={fieldValue || ''}
            onChange={(val) => onConfigChange(selectedNode.id, field.key, val)}
            dialect={config.dialect || 'db2'}
            disabled={executing}
            height="300px"
            placeholder={field.description || 'Paste or upload SQL scripts...'}
          />
        )

      default:
        return (
          <input
            type="text"
            value={fieldValue || ''}
            onChange={(e) =>
              onConfigChange(selectedNode.id, field.key, e.target.value)
            }
            placeholder={field.placeholder || ''}
            disabled={executing}
          />
        )
    }
  }

  // Determine category label from nodeDef
  const categoryLabel = nodeDef.category
    ? nodeDef.category.charAt(0).toUpperCase() + nodeDef.category.slice(1)
    : 'Node'

  return (
    <div className="etl-config-panel">
      {/* Header */}
      <div className="etl-config-header">
        <span
          className="etl-config-badge"
          style={{
            backgroundColor: `${nodeDef.color || '#8b949e'}20`,
            color: nodeDef.color || '#8b949e',
            borderColor: `${nodeDef.color || '#8b949e'}40`,
          }}
        >
          {categoryLabel}
        </span>
        <h3 className="etl-config-title">
          {selectedNode.data?.label || nodeDef.label || 'Node'}
        </h3>
        <p className="etl-config-description">
          {nodeDef.description || ''}
        </p>
      </div>

      {/* Config body */}
      <div className="etl-config-body">
        {/* Node Settings (always present) */}
        <div className="etl-config-section">
          <div
            className="etl-config-section-header"
            onClick={() => toggleSection('settings')}
          >
            <span>Node Settings</span>
            {expandedSections.settings !== false ? (
              <LucideIcons.ChevronDown size={14} />
            ) : (
              <LucideIcons.ChevronRight size={14} />
            )}
          </div>
          {expandedSections.settings !== false && (
            <div className="etl-config-section-body">
              <div className="etl-config-field">
                <label>Label</label>
                <input
                  type="text"
                  value={selectedNode.data?.label || ''}
                  onChange={(e) =>
                    onConfigChange(
                      selectedNode.id,
                      '_label',
                      e.target.value
                    )
                  }
                  disabled={executing}
                />
              </div>
              <label className="etl-checkbox">
                <input
                  type="checkbox"
                  checked={selectedNode.data?.enabled !== false}
                  onChange={(e) =>
                    onConfigChange(
                      selectedNode.id,
                      '_enabled',
                      e.target.checked
                    )
                  }
                  disabled={executing}
                />
                <span>Enabled</span>
              </label>
            </div>
          )}
        </div>

        {/* Connection Manager — source and target nodes only */}
        {isSourceOrTarget && (
          <div className="etl-config-section">
            <div
              className="etl-config-section-header"
              onClick={() => toggleSection('connection')}
            >
              <LucideIcons.Link size={14} />
              <span>Connection</span>
              {expandedSections.connection !== false ? (
                <LucideIcons.ChevronDown size={14} />
              ) : (
                <LucideIcons.ChevronRight size={14} />
              )}
            </div>
            {expandedSections.connection !== false && (
              <div className="etl-config-section-body">
                {connName ? (
                  <div className="etl-connection-status">
                    <div className="etl-connection-status-info">
                      <LucideIcons.CheckCircle size={14} style={{ color: '#3fb950' }} />
                      <div>
                        <div className="etl-connection-status-name">{connName}</div>
                        <div className="etl-connection-status-driver">
                          {connDriver?.name || connDriverId}
                          {connSnapshot?.host && ` — ${connSnapshot.host}:${connSnapshot.port || ''}`}
                        </div>
                      </div>
                    </div>
                    <button
                      className="etl-connection-disconnect"
                      onClick={() => {
                        onConfigChange(selectedNode.id, '_connectionId', null)
                        onConfigChange(selectedNode.id, '_connectionSnapshot', null)
                      }}
                      disabled={executing}
                      title="Disconnect"
                    >
                      <LucideIcons.Unlink size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="etl-connection-empty">
                    <LucideIcons.AlertCircle size={14} style={{ color: '#d29922' }} />
                    <span>No connection configured</span>
                  </div>
                )}
                <button
                  className="etl-open-connection-btn"
                  onClick={() => onOpenConnectionManager?.(selectedNode)}
                  disabled={executing}
                >
                  <LucideIcons.Settings size={14} />
                  {connName ? 'Change Connection' : 'Open Connection Manager'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Per-section config fields */}
        {Object.entries(groupedFields).map(([section, fields]) => (
          <div key={section} className="etl-config-section">
            <div
              className="etl-config-section-header"
              onClick={() => toggleSection(section)}
            >
              <span>{section}</span>
              {expandedSections[section] !== false ? (
                <LucideIcons.ChevronDown size={14} />
              ) : (
                <LucideIcons.ChevronRight size={14} />
              )}
            </div>
            {expandedSections[section] !== false && (
              <div className="etl-config-section-body">
                {fields.map((field) => (
                  <div key={field.key} className="etl-config-field">
                    {field.type !== 'checkbox' && (
                      <label>{field.label}</label>
                    )}
                    {field.description && (
                      <span className="etl-field-hint">
                        {field.description}
                      </span>
                    )}
                    {renderField(field, config[field.key])}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Output Schema */}
        <div className="etl-config-section">
          <div
            className="etl-config-section-header"
            onClick={() => toggleSection('output_schema')}
          >
            <LucideIcons.Database size={14} />
            <span>Output Schema</span>
            {expandedSections.output_schema !== false ? (
              <LucideIcons.ChevronDown size={14} />
            ) : (
              <LucideIcons.ChevronRight size={14} />
            )}
          </div>
          {expandedSections.output_schema !== false && (
            <div className="etl-config-section-body">
              <SchemaEditor
                schema={selectedNode.data?.outputSchema || null}
                onChange={(newSchema) => {
                  onConfigChange(selectedNode.id, '_outputSchema', newSchema)
                }}
                readOnly={!isSourceOrTarget && nodeDef.category !== 'utility'}
                disabled={executing}
              />
            </div>
          )}
        </div>

        {/* Execution Results */}
        {(selectedNode.data?.executionResult || progress?.result) && (
          <div className="etl-config-section">
            <div
              className="etl-config-section-header"
              onClick={() => toggleSection('results')}
            >
              <span>Results</span>
              {expandedSections.results !== false ? (
                <LucideIcons.ChevronDown size={14} />
              ) : (
                <LucideIcons.ChevronRight size={14} />
              )}
            </div>
            {expandedSections.results !== false && (
              <div className="etl-config-section-body">
                <div className="etl-result-item">
                  Status:{' '}
                  {progress?.status || selectedNode.data?.status || 'N/A'}
                </div>
                {result?.row_count != null && (
                  <div className="etl-result-item">
                    Rows: {result.row_count.toLocaleString()}
                  </div>
                )}
                {result?.duration && (
                  <div className="etl-result-item">
                    Duration: {result.duration}
                  </div>
                )}
                {result?.error && (
                  <div className="etl-result-error">{result.error}</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="etl-config-footer">
        <button
          className="etl-delete-btn"
          onClick={() => onDeleteNode(selectedNode.id)}
          disabled={executing}
        >
          <LucideIcons.Trash2 size={14} />
          Delete Node
        </button>
      </div>
    </div>
  )
}

export default EtlConfigPanel
